import { useState } from 'react';
import { FolderSummary } from '../../api/access';
import { Modal, RowMenu, SectionLabel } from '../../components/fs';

/**
 * The 220px rail beside the forms list (R2, screen 06).
 *
 * Two fixed entries, then the account's folders. "Unfiled" is one of the fixed
 * ones rather than a folder row, because a form with no folder is in a STATE,
 * not in a place — so it cannot be renamed, shared or deleted, and a brand-new
 * account needs nothing seeded for the rail to be correct.
 *
 * Two states the rail must not get wrong, both of which happen on rollout
 * morning (canvas 3):
 *
 *   * an account with NO folders — the section below the fixed entries becomes
 *     an invitation at the same width, so the rail is never one lonely item
 *     pretending to be navigation (3C).
 *   * a Member with no folders shared — one 12px line, not an empty list (3A).
 *
 * "Shared with me" appears for Members only: for an Admin every form is shared
 * with them, so the entry would mean nothing.
 */
export type RailSelection = 'all' | 'shared' | 'unfiled' | number;

export default function FolderRail({
  folders, total, unfiled, selection, onSelect, isMember, canManageFolders,
  onNewFolder, onRenameFolder, onShareFolder, onDeleteFolder,
}: {
  folders: FolderSummary[];
  /** every form the caller can reach — an Admin's "all", a Member's "shared" */
  total: number;
  unfiled: number;
  selection: RailSelection;
  onSelect: (next: RailSelection) => void;
  isMember: boolean;
  canManageFolders: boolean;
  onNewFolder: () => void;
  onRenameFolder: (f: FolderSummary) => void;
  onShareFolder: (f: FolderSummary) => void;
  onDeleteFolder: (f: FolderSummary) => void;
}) {
  // One open menu at a time, per rule 5 — the rail is a one-column table.
  const [menuId, setMenuId] = useState<number | null>(null);

  const item = (key: RailSelection, label: string, count: number) => (
    <button
      key={String(key)}
      type="button"
      className={`fs-rail-item${selection === key ? ' on' : ''}`}
      aria-current={selection === key ? 'true' : undefined}
      onClick={() => onSelect(key)}
    >
      <span className="label">{label}</span>
      <span className="n">{count}</span>
    </button>
  );

  return (
    <nav className="fl-rail" aria-label="Folders">
      {item('all', 'All forms', total)}
      {isMember && item('shared', 'Shared with me', total)}
      {item('unfiled', 'Unfiled', unfiled)}

      {folders.length > 0 && <SectionLabel>Folders</SectionLabel>}

      {folders.map((f) => (
        <div key={f.id} className="fl-rail-row">
          <button
            type="button"
            className={`fs-rail-item${selection === f.id ? ' on' : ''}`}
            aria-current={selection === f.id ? 'true' : undefined}
            onClick={() => onSelect(f.id)}
          >
            <span className="label">{f.name}</span>
            <span className="n">{f.form_count}</span>
          </button>
          {canManageFolders && (
            <RowMenu open={menuId === f.id} onToggle={(o) => setMenuId(o ? f.id : null)} label={f.name}>
              <button onClick={() => onRenameFolder(f)}>Rename…</button>
              <button onClick={() => onShareFolder(f)}>Share…</button>
              <div className="rule" />
              <button className="danger" onClick={() => onDeleteFolder(f)}>Delete folder…</button>
            </RowMenu>
          )}
        </div>
      ))}

      {/* 3C: never one lonely item. The invitation replaces the folder list
          entirely, and stops being an invitation the moment a folder exists. */}
      {!folders.length && canManageFolders && (
        <div className="fl-rail-invite">
          <div className="fs-rail-note">
            Folders group forms so they are easier to find — and they are the
            easiest way to share a set of forms with the same people.
          </div>
          <button type="button" className="fs-btn neutral sm" style={{ width: '100%' }} onClick={onNewFolder}>
            + New folder
          </button>
        </div>
      )}

      {/* 3A: a Member with nothing filed says so in a sentence, rather than
          leaving a caption over an empty list. */}
      {!folders.length && !canManageFolders && (
        <div className="fs-rail-note">No folders have been shared with you yet.</div>
      )}

      {!!folders.length && canManageFolders && (
        <button type="button" className="fs-rail-item fl-rail-new" onClick={onNewFolder}>
          <span className="label">+ New folder</span>
        </button>
      )}
    </nav>
  );
}

/**
 * The same navigation on a phone (D2): a scrolling row of chips instead of a
 * 220px rail, because a rail beside a list leaves the list ~110px wide.
 *
 * A chip carries only what it is and how many are in it. The per-folder
 * actions — rename, share, delete — are a finger's width apart in a rail, so
 * they move into `FolderSheet` behind one "Manage folders" chip rather than
 * riding every row.
 */
export function FolderChips({
  folders, total, unfiled, selection, onSelect, isMember, canManageFolders, onNewFolder, onManage,
}: {
  folders: FolderSummary[];
  total: number;
  unfiled: number;
  selection: RailSelection;
  onSelect: (next: RailSelection) => void;
  isMember: boolean;
  canManageFolders: boolean;
  onNewFolder: () => void;
  onManage: () => void;
}) {
  const chip = (key: RailSelection, label: string, count: number) => (
    <button
      key={String(key)}
      type="button"
      className={`fl-chip${selection === key ? ' on' : ''}`}
      aria-pressed={selection === key}
      onClick={() => onSelect(key)}
    >
      {label} <span className="n">{count}</span>
    </button>
  );

  return (
    <div className="fl-chips" role="group" aria-label="Folders">
      {chip('all', 'All forms', total)}
      {isMember && chip('shared', 'Shared with me', total)}
      {chip('unfiled', 'Unfiled', unfiled)}
      {folders.map((f) => chip(f.id, f.name, f.form_count))}
      {canManageFolders && (
        folders.length
          ? <button type="button" className="fl-chip add" onClick={onManage}>Manage folders</button>
          : <button type="button" className="fl-chip add" onClick={onNewFolder}>+ New folder</button>
      )}
    </div>
  );
}

/** The rail's ⋯ actions, as a sheet, plus the way to make another folder. */
export function FolderSheet({ folders, onClose, onNewFolder, onRenameFolder, onShareFolder, onDeleteFolder }: {
  folders: FolderSummary[];
  onClose: () => void;
  onNewFolder: () => void;
  onRenameFolder: (f: FolderSummary) => void;
  onShareFolder: (f: FolderSummary) => void;
  onDeleteFolder: (f: FolderSummary) => void;
}) {
  return (
    <Modal
      title="Manage folders" narrow onClose={onClose}
      footer={<>
        <button className="fs-btn primary" onClick={() => { onClose(); onNewFolder(); }}>+ New folder</button>
        <button className="fs-btn neutral" onClick={onClose}>Done</button>
      </>}
    >
      {folders.map((f) => (
        <div className="fl-manage-row" key={f.id}>
          <span className="name">
            {f.name}
            <span className="fs-cell-sub">
              {f.form_count} {f.form_count === 1 ? 'form' : 'forms'}
              {' · '}
              {f.base_access === 'everyone'
                ? `everyone in the account can ${f.base_level}`
                : 'restricted to the people it is shared with'}
            </span>
          </span>
          <button className="fs-btn neutral sm" onClick={() => { onClose(); onRenameFolder(f); }}>Rename</button>
          <button className="fs-btn neutral sm" onClick={() => { onClose(); onShareFolder(f); }}>Share</button>
          <button className="fs-btn danger sm" onClick={() => { onClose(); onDeleteFolder(f); }}>Delete</button>
        </div>
      ))}
    </Modal>
  );
}
